// @flow
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import '@uppy/webcam/dist/style.min.css';

import React, {Component} from 'react';
import ImageToMRZ, {isMobile} from './lib';

import Uppy from '@uppy/core';
import {Dashboard} from '@uppy/react';
import Webcam from '@uppy/webcam';
import {GradientPage} from '../../../components/common';
import config from '../../../config';
import I18n from '../../../lib/i18n';
import ErrorModal from './components/ErrorModal';
import PageTitle from '../../../components/signUp/PageTitle.web';
import WizzeeLog from '../../../lib/wizzeeLog';

type State = {
  errorText: string,
  isMobile?: boolean | null,
  numberOfErrors: number,
  uppyInstance?: Object,
};

type Props = {
  goToConfirmPersonalInfo: Function,
  storePassportInfo: Function,
};

export default class PassportCheck extends Component<Props, State> {
  state: State = {
    errorText: '',
    isMobile: null,
    numberOfErrors: 0,
  };

  handleErrorModalMaxTries = () => {
    this.setState({errorText: ''});
    this.props.storePassportInfo(null, '');
    this.props.goToConfirmPersonalInfo(true);
  };

  handleTryAgain = () => {
    this.setState({errorText: ''});
    if (this.state.uppyInstance) {
      this.state.uppyInstance.cancelAll();
    }
  };

  uppy = () =>
    new Uppy({
      id: 'uppy',
      autoProceed: false,
      debug: true,
      restrictions: {
        maxNumberOfFiles: 1,
        minNumberOfFiles: 1,
        allowedFileTypes: [
          '.jpg',
          '.JPG',
          '.jpeg',
          '.JPEG',
          '.png',
          '.PNG',
          '.pdf',
          '.PDF',
        ],
        maxFileSize: 10000000,
      },
      onBeforeUpload: files => {
        // $FlowFixMe
        document.body.style.cursor = 'wait';
        this.convertToBase64(files);
      },
    }).use(Webcam, {
      modes: ['picture'],
      facingMode: this.state.isMobile ? 'environment' : 'user',
      mirror: !this.state.isMobile,
      title: I18n.t('SignUp.PassportCheck.camera'),
      locale: {
        strings: {
          takePicture: I18n.t('SignUp.PassportCheck.takePicture'),
          allowAccessTitle: I18n.t('SignUp.PassportCheck.allowAccessTitle'),
          allowAccessDescription: I18n.t(
            'SignUp.PassportCheck.allowAccessDescription',
          ),
        },
      },
    });

  scanWeb = (fileContentBase64: string) => {
    const image = {
      base64: fileContentBase64,
    };

    ImageToMRZ(image)
      .then(result => {
        this.props.storePassportInfo(result.mrzResult, result.base64Image);
        this.props.goToConfirmPersonalInfo(false, true);
      })
      .catch(error => {
        WizzeeLog.warn('Error scanning ID', error);

        if (this.state.numberOfErrors + 1 >= config.MAX_SCAN_TRIES) {
          this.props.storePassportInfo(null, '');
          this.props.goToConfirmPersonalInfo(true, false);
        } else {
          this.setState((prevState: State) => ({
            errorText:
              error === 'Timeout'
                ? I18n.t('SignUp.IdScan.modalError.timeout')
                : I18n.t('SignUp.IdScan.modalError.invalid'),
            numberOfErrors: ++prevState.numberOfErrors,
          }));
        }
      })
      .finally(() => {
        // $FlowFixMe
        document.body.style.cursor = 'default';
      });
  };

  convertToBase64 = (files: Object[]) => {
    const reader: Object = new FileReader();
    const file: Object = Object.values(files)[0];
    if (file) {
      reader.readAsDataURL(file.data);
      reader.onloadend = () => {
        const base64: string[] = reader.result.split(',');
        base64[1] ? this.scanWeb(base64[1]) : this.scanWeb(base64[0]);
      };
    }
  };

  componentDidMount() {
    this.setState({isMobile: isMobile(window.navigator.userAgent)}, () => {
      const uppyInstance = this.uppy();
      this.setState({uppyInstance});
    });
  }

  componentWillUnmount() {
    if (this.state.uppyInstance) {
      this.state.uppyInstance.close();
    }
  }

  render() {
    if (!this.state.uppyInstance) return null;

    return (
      <GradientPage maxWidth={600}>
        <PageTitle title={I18n.t('SignUp.IdentityRequirements.title')} />
        <Dashboard
          uppy={this.state.uppyInstance}
          target="body"
          plugins={['Webcam']}
          inline={true}
          width={'100%'}
          showProgressDetails={true}
          note={I18n.t('SignUp.PassportCheck.description')}
          locale={{
            strings: {
              dropPaste: I18n.t('SignUp.PassportCheck.dropPaste', {
                browse: '%{browse}',
              }),
              browse: I18n.t('SignUp.PassportCheck.browse'),
              myDevice: I18n.t('SignUp.PassportCheck.myDevice'),
              cancel: I18n.t('SignUp.PassportCheck.cancel'),
              uploadXFiles: {
                '0': I18n.t('SignUp.PassportCheck.uploadXFiles', {
                  fileCount: '%{smart_count}',
                }),
                '1': I18n.t('SignUp.PassportCheck.uploadXFiles', {
                  fileCount: '%{smart_count}',
                }),
              },
              xFilesSelected: {
                '0': I18n.t('SignUp.PassportCheck.xFilesSelected', {
                  fileCount: '%{smart_count}',
                }),
                '1': I18n.t('SignUp.PassportCheck.xFilesSelected', {
                  fileCount: '%{smart_count}',
                }),
              },
            },
          }}
          proudlyDisplayPoweredByUppy={false}
        />
        <ErrorModal
          buttonText={I18n.t('SignUp.IdScan.modalError.button')}
          content={I18n.t('SignUp.IdScan.modalError.tip')}
          handleMaxTries={this.handleErrorModalMaxTries}
          hasReachedMaxTries={
            this.state.numberOfErrors >= config.MAX_SCAN_TRIES - 1
          }
          isVisible={!!this.state.errorText}
          onPress={this.handleTryAgain}
          title={this.state.errorText}
        />
      </GradientPage>
    );
  }
}
